import { CommonApiService } from '@services/common-api-service';
import { Component, Event, EventEmitter, Host, Prop, State, h } from '@stencil/core';
import { If } from '@utils/If';
import { z } from 'zod';
import { PathMap } from '../../../config';
import { ConfigService } from '../../../services/config-service';

const CustomerSchema = z
  .object({
    mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid mobile number'),
    lastFourDigits: z
      .string()
      .min(1, 'Last 4 digits are required.')
      .regex(/^\d{4}$/, 'Enter the valid last 4 digits of your card.')
      .transform(val => val.trim()),
    dob: z.string().min(1, 'Date of birth is required.')
  })
  .superRefine((data, ctx) => {
    const date = new Date(data.dob);

    if (isNaN(date.getTime())) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Invalid date format.'
      });
      return;
    }

    const now = new Date();
    if (date > now) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Date of birth cannot be in the future.'
      });
    }

    const age = now.getFullYear() - date.getFullYear();
    const hadBirthdayThisYear = now.getMonth() > date.getMonth() || (now.getMonth() === date.getMonth() && now.getDate() >= date.getDate());

    const actualAge = hadBirthdayThisYear ? age : age - 1;

    if (actualAge < 18) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'You must be at least 18 years old.'
      });
    }

    if (actualAge > 120) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date of birth.'
      });
    }
  });

type CustomerFormInput = {
  mobile: string;
  lastFourDigits: string;
  dob: string;
};
@Component({
  tag: 'add-card',
  styleUrl: 'add-card.scss',
  shadow: true
})
export class AddCard {
  @Prop() assetPath: string;
  @Prop() customerInfo: any = {};
  @Prop() addCardTitle: string = '';
  @Prop() loginInfo: any = {};
  @Prop() isAddCardRequired: boolean = false;
  @Event() addCardSuccess: EventEmitter<void>;
  @Event() cancelAddCard: EventEmitter<void>;
  @Event() closeAddCard: EventEmitter<void>;
  @State() open: boolean = true;
  @State() verifying: boolean = false;
  @State() cardVerified: boolean = false;
  @State() cardDetails: any = {};
  @State() submitted = false;
  cardFaciaUrl: string = ConfigService.cardFaciaCdnUrl || '';

  private apiService = new CommonApiService();
  @State() errors: Record<keyof CustomerFormInput, string> & { generic?: string } = {
    mobile: '',
    lastFourDigits: '',
    dob: '',
    generic: ''
  };
  @State() form: CustomerFormInput = {
    mobile: '',
    lastFourDigits: '',
    dob: ''
  };
  @State() touched: Record<keyof CustomerFormInput, boolean> = {
    mobile: false,
    lastFourDigits: false,
    dob: false
  };

  private updateField(field: keyof CustomerFormInput, value: string) {
    this.form = { ...this.form, [field]: value };
    this.errors = { ...this.errors, generic: '' };
    if (this.touched[field] || this.submitted) {
      const error = this.validateField(field, value);
      this.errors = { ...this.errors, [field]: error };
    }
  }

  private validateField(field: keyof CustomerFormInput, value: string): string {
    const partial = { ...this.form, [field]: value };
    const result = CustomerSchema.safeParse(partial);
    if (!result.success) {
      const issue = result.error.issues.find(i => i.path[0] === field);
      return issue ? issue.message : '';
    }
    return '';
  }

  private handleBlur(field: keyof CustomerFormInput) {
    this.touched = { ...this.touched, [field]: true };
    const error = this.validateField(field, this.form[field]);
    this.errors = { ...this.errors, [field]: error };
  }

  verifyCardDetails = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    this.submitted = true;

    const errors: Record<keyof CustomerFormInput, string> = {
      lastFourDigits: this.validateField('lastFourDigits', this.form.lastFourDigits),
      mobile: this.validateField('mobile', this.form.mobile),
      dob: this.validateField('dob', this.form.dob)
    };
    this.errors = errors;

    if (Object.values(errors).some(e => e)) {
      return;
    }
    try {
      this.verifying = true;
      let body = {}
      const paths = Object.values(PathMap).map(path => String(path));
      const converted = Object.entries(PathMap).reduce((acc, [key, value]) => {
        acc[value] = key;
        return acc;
      }, {} as Record<string, string>);
      const currentUrl = window.location.pathname;
      const segments = currentUrl.split('/').filter(segment => segment);
      const matchedPath = segments.find(segment => paths.includes(segment)) || '';
      if (this.loginInfo?.viaOtpResponse) {
        body = {
          last4digit: this.form.lastFourDigits,
          mobile: this.form.mobile,
          DOB: this.form.dob,
          type: 'available_points',
          modal: 'DIGITAL',
          services_id: 1,
          partner_id: 1,
          noopt: 1,
          savecard: 1,
          customer_id: this.loginInfo['customer_id'],
          programName: converted[matchedPath],
          _token: this.loginInfo['XSRF-TOKEN'],
          user_id: this.loginInfo['id'],
          clientToken: converted[matchedPath]
        };
      } else {
        body = {
          last4digit: this.form.lastFourDigits,
          mobile: this.form.mobile,
          DOB: this.form.dob,
          type: 'available_points',
          modal: 'DIGITAL',
          services_id: 1,
          partner_id: 1,
          noopt: 1,
          savecard: 1,
          customer_id: this.customerInfo['customer_id'],
          programName: converted[matchedPath] || 'SMARTBUY',
          _token: this.customerInfo['XSRF-TOKEN'],
          user_id: this.customerInfo['id'],
          clientToken: converted[matchedPath] || 'SMARTBUY'
        };
      }

      const endpoint = matchedPath ? `/${matchedPath}/api/availablepoints` : '/api/availablepoints';
      const result = await this.apiService.post(endpoint, body);
      if (result['status'] === 'true') {
        this.cardVerified = true;
        const cardDetails = {
          ...result,
          bin: result.bin,
          lastFour: result.last4digit,
          logoId: result.logo_id,
          cardType: result.client_type,
          subProgram: result.sub_program
        };
        this.cardDetails = cardDetails;
      } else {
        this.errors['postError'] = result.msg || result.message || 'Something went wrong. Please try after sometime';
      }
    } catch (error) {
      this.errors['postError'] = 'Something went wrong. Please try after sometime';
    } finally {
      this.verifying = false;
    }
  };

  onCancel = () => {
    this.open = false;
    setTimeout(() => {
      this.cancelAddCard.emit();
    }, 1000);
  };

  private get maxDob() {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    // Format as YYYY-MM-DD
    return today.toISOString().split('T')[0];
  }

  onClose = () => {
    this.open = false;
    setTimeout(() => {
      this.closeAddCard.emit(this.cardDetails);
    }, 1000);
  };

  onImageError = (event: any) => {
    if (event.target.dataset.fallback) return; // already tried fallback
    event.target.onerror = null;
    event.target.src = `${this.cardFaciaUrl}default.webp`;
    event.target.dataset.fallback = 'true';
  }

  render() {
    return (
      <Host>
        <popup-modal open={this.open} size="md">
          <If
            condition={!this.cardVerified}
            children={
              <form onSubmit={this.verifyCardDetails} class="bg-white p-6 md:p-8">
                <div class="flex flex-col gap-5 md:gap-4">
                  <h5 class="font-semibold text-black text-xl md:text-xl text-center">{this.addCardTitle || 'Add Your Card Details'}</h5>
                  <div class="flex flex-col gap-5">
                    <input-field
                      autoFocus={this.form.lastFourDigits.length !== 4}
                      value={this.form.lastFourDigits}
                      error={this.errors['lastFourDigits']}
                      onValueChanged={e => this.updateField('lastFourDigits', e.detail)}
                      label="Last 4 digits of your card"
                      restrictInput={true}
                      inputType="number"
                      name="lastFourDigits"
                      maxLength={4}
                      onBlur={() => this.handleBlur('lastFourDigits')}
                    />
                    <phone-input
                      autoFocus={this.form.lastFourDigits.length === 4}
                      value={this.form.mobile}
                      error={this.errors['mobile']}
                      onValueChanged={e => this.updateField('mobile', e.detail.mobile)}
                      label="Mobile Number"
                      name="number"
                      onBlur={() => this.handleBlur('mobile')}
                    />
                    <input-field
                      autoFocus={this.form.mobile.length === 10}
                      value={this.form.dob}
                      error={this.errors.dob}
                      onValueChanged={e => this.updateField('dob', e.detail)}
                      type="date"
                      label="Date of Birth"
                      name="dob"
                      max={this.maxDob}
                      onBlur={() => this.handleBlur('dob')}
                    />
                    <input type="submit" class="hidden" />
                  </div>
                  <div>
                    <If condition={!!this.errors['postError']} children={<span class="text-red-500 text-xs mb-2 block">{this.errors['postError']}</span>} />
                    <div class="flex flex-col-reverse md:flex-row gap-4">
                      <If
                        condition={!this.isAddCardRequired}
                        children={
                          <custom-button
                            onButtonClick={this.onCancel}
                            class="flex-1"
                            variant="outlined"
                          >
                            Cancel
                          </custom-button>
                        }
                      />
                      <custom-button isLoading={this.verifying} disabled={this.verifying} class="flex-1" type="submit" onButtonClick={this.verifyCardDetails}>
                        Confirm
                      </custom-button>
                    </div>
                  </div>
                </div>
              </form>
            }
          />
          <If
            condition={this.cardVerified}
            children={
              <div class="flex flex-col p-7 items-center">
                <img class="w-20" src={this.assetPath + 'icons/success.svg'} />
                <h5 class="font-semibold text-black text-xl mt-8 mb-6">Your Card Is Successfully Added</h5>
                <div class="p-3 rounded-lg border-solid border border-gray-300 bg-sb-primary-100 gap-4 flex items-center">
                  <img onError={this.onImageError} width={60} height={40} src={this.cardFaciaUrl + this.cardDetails.card_facia} alt="" />
                  <div>
                    <p class="font-medium text-gray-800">{this.cardDetails.alter_name}</p>
                    <span class="text-gray-500">**** {this.cardDetails.last4digit}</span>
                  </div>
                </div>
                <custom-button onButtonClick={this.onClose} class="w-full md:w-70 mt-7 md:mt-10">
                  Close
                </custom-button>
              </div>
            }
          />
        </popup-modal>
      </Host>
    );
  }
}
